import {
  Dispatch,
  SetStateAction,
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';
import { FormattedMessage, useIntl } from 'react-intl';
import { DateTime } from 'luxon';
import {
  useGetUserAllowedOrganizationOfferGroups,
  useGetUserAllowedOrganizationTags,
} from '@/api/trafikportalen';
import { CollectionTicketClaimRevokeStrategyDTO } from '@/api/trafikportalen/models/collectionTicketClaimRevokeStrategyDTO';
import Button from '../core/Button';
import Input from '../core/Input';
import ToggleButtonGroup from '../core/ToggleButtonGroup';
import { DialogWindow } from '../Dialog';
import { ErrorBanner } from '../core/ErrorBanner';
import { WarningBanner } from '../core/WarningBanner';
import { SelectableList } from '../SelectableList';
import { collator } from '@/utils/collator';
import {
  toPeriodEndTimestamp,
  toPeriodStartTimestamp,
} from '@/helpers/periodEndDate';
import { findClaimsWithSameConfiguration } from '@/helpers/periodConfiguration';
import { findOverlappingClaims } from '@/helpers/periodOverlap';
import { hasRelationalProducts } from '@/helpers/relationalProducts';
import { PeriodOverlapWarning } from './PeriodOverlapWarning';
import type { CollectionTicketClaimDTO } from '@/api/trafikportalen';
import type { CollectionTicketClaimTypeDTO } from '@/api/trafikportalen/models/collectionTicketClaimTypeDTO';
import type { CollectionTypeDTO } from '@/api/trafikportalen/models/collectionTypeDTO';
import type { ToggleButtonOption } from '../core/ToggleButtonGroup';
import { IconCheck } from '@/icons';

export interface CreatePeriodFormData {
  valid_from: string;
  valid_to: string;
  type: CollectionTicketClaimTypeDTO;
  revoke_strategy: CollectionTicketClaimRevokeStrategyDTO;
  reissuance_allowed: boolean;
  relational: boolean;
  stina_product_id?: string;
}

interface CreatePeriodDialogProps {
  open: boolean;
  onOpenChange: Dispatch<SetStateAction<boolean>>;
  onSubmit: (data: CreatePeriodFormData) => Promise<void>;
  isSubmitting: boolean;
  claimType: CollectionTicketClaimTypeDTO;
  collectionType: CollectionTypeDTO;
  existingClaims?: CollectionTicketClaimDTO[];
}

type OfferSelectionUiMode = 'loading' | 'empty' | 'single' | 'multiple';

export const CreatePeriodDialog = ({
  open,
  onOpenChange,
  onSubmit,
  isSubmitting,
  claimType,
  collectionType,
  existingClaims,
}: CreatePeriodDialogProps) => {
  const { formatMessage } = useIntl();

  const { data: offerGroups, isLoading: isLoadingOffers } =
    useGetUserAllowedOrganizationOfferGroups();
  const { data: allowedTags, isError: tagsAccessFailed } =
    useGetUserAllowedOrganizationTags();

  const offerOptions = useMemo(() => {
    if (!offerGroups) return [];

    return offerGroups
      .filter((group) => group.collection_type === collectionType)
      .map((group) => ({
        id: group.group_key,
        label: group.title,
      }))
      .sort((a, b) => collator.compare(a.label, b.label));
  }, [offerGroups, collectionType]);

  const offerSelectionUiMode = useMemo((): OfferSelectionUiMode => {
    if (isLoadingOffers) return 'loading';
    if (offerOptions.length === 0) return 'empty';
    if (offerOptions.length === 1) return 'single';
    return 'multiple';
  }, [isLoadingOffers, offerOptions]);

  const today = DateTime.now().toFormat('yyyy-MM-dd');

  const [formFrom, setFormFrom] = useState(today);
  const [formTo, setFormTo] = useState('');
  const [formRevokeStrategy, setFormRevokeStrategy] =
    useState<CollectionTicketClaimRevokeStrategyDTO>('revoke');
  const isChildAndYouth = collectionType === 'school-child-and-youth';
  const [formRelational, setFormRelational] = useState(!isChildAndYouth);
  const relational = formRelational && !isChildAndYouth;
  const organizationHasRelationalProducts = allowedTags
    ? hasRelationalProducts(allowedTags, collectionType)
    : undefined;
  const [formOfferStinaProductId, setFormOfferStinaProductId] = useState<
    string | null
  >(null);

  const handleSelectOffer = useCallback((productSetId: string) => {
    setFormOfferStinaProductId(productSetId);
  }, []);

  const parsedStinaProductId = useMemo<string | null>(() => {
    if (!formOfferStinaProductId) return null;
    const underscoreIndex = formOfferStinaProductId.indexOf('_');
    if (underscoreIndex === -1) return null;
    const productId = formOfferStinaProductId.slice(underscoreIndex + 1);
    return productId.length > 0 ? productId : null;
  }, [formOfferStinaProductId]);

  const offerFormatError =
    !relational && formOfferStinaProductId && !parsedStinaProductId
      ? formatMessage({
          defaultMessage:
            'Erbjudandet har ett oväntat format och kan inte användas. Kontakta support.',
        })
      : undefined;

  const resetForm = useCallback(() => {
    setFormFrom(today);
    setFormTo('');
    setFormRevokeStrategy('revoke');
    setFormRelational(!isChildAndYouth);
    setFormOfferStinaProductId(null);
  }, [isChildAndYouth, today]);

  // Reset when the dialog opens. This effect must run before the offer
  // pre-selection below: when the dialog mounts already open with offers
  // loaded, both effects run in the same commit and the last state update
  // wins, so the pre-selection has to come after the reset.
  const wasOpenRef = useRef(false);
  useEffect(() => {
    if (open && !wasOpenRef.current) {
      resetForm();
    }
    wasOpenRef.current = open;
  }, [open, resetForm]);

  useEffect(() => {
    if (relational || offerOptions.length === 0) return;

    const firstId = offerOptions[0].id;

    if (offerOptions.length === 1) {
      if (formOfferStinaProductId !== firstId) {
        setFormOfferStinaProductId(firstId);
      }
      return;
    }

    const hasValidSelection =
      formOfferStinaProductId !== null &&
      offerOptions.some((o) => o.id === formOfferStinaProductId);

    if (!hasValidSelection) {
      setFormOfferStinaProductId(firstId);
    }
  }, [relational, offerOptions, formOfferStinaProductId]);

  const revokeStrategyOptions: ToggleButtonOption[] = useMemo(
    () => [
      {
        value: 'revoke',
        label: formatMessage({ defaultMessage: 'Makulera' }),
        tooltip: formatMessage({
          defaultMessage: 'Biljetter makuleras utan återbetalning',
        }),
      },
      {
        value: 'none',
        label: formatMessage({ defaultMessage: 'Inget' }),
        tooltip: formatMessage({
          defaultMessage: 'Biljetter makuleras inte',
        }),
      },
    ],
    [formatMessage],
  );

  const ticketKindOptions: ToggleButtonOption[] = useMemo(
    () => [
      {
        value: 'relational',
        label: formatMessage({ defaultMessage: 'Relationsbunden' }),
        tooltip: formatMessage({
          defaultMessage:
            'Nyttja adressinformationen på varje elev för att automatiskt söka fram rätt biljett. Kommer välja den billigaste som täcker elevens behov.',
        }),
      },
      {
        value: 'static',
        label: formatMessage({ defaultMessage: 'Statisk' }),
        tooltip: formatMessage({
          defaultMessage: 'Alla elever får samma biljett.',
        }),
      },
    ],
    [formatMessage],
  );

  const fromError = useMemo(() => {
    if (!formFrom) return undefined;
    if (formFrom < today)
      return formatMessage({
        defaultMessage: 'Startdatum måste vara dagens datum eller senare',
      });
    return undefined;
  }, [formFrom, today, formatMessage]);

  const toError = useMemo(() => {
    if (!formTo) return undefined;
    if (formFrom && formTo <= formFrom)
      return formatMessage({
        defaultMessage: 'Slutdatum måste vara efter startdatum',
      });
    return undefined;
  }, [formFrom, formTo, formatMessage]);

  const periodDraft = useMemo(() => {
    if (!formFrom || !formTo || toError) return null;
    if (!relational && !parsedStinaProductId) return null;
    return {
      valid_from: toPeriodStartTimestamp(formFrom),
      valid_to: toPeriodEndTimestamp(formTo, false),
      type: claimType,
      revoke_strategy: formRevokeStrategy,
      reissuance_allowed: true,
      relational: relational,
      ...(!relational && parsedStinaProductId
        ? { stina_product_id: parsedStinaProductId }
        : {}),
    };
  }, [
    formFrom,
    formTo,
    toError,
    relational,
    parsedStinaProductId,
    claimType,
    formRevokeStrategy,
  ]);

  const duplicateClaims = useMemo(() => {
    if (!periodDraft) return [];
    return findClaimsWithSameConfiguration(existingClaims ?? [], periodDraft);
  }, [periodDraft, existingClaims]);

  // Overlaps are allowed (the backend accepts them) but the user is warned.
  // An exact duplicate is reported separately and blocks creation.
  const overlappingClaims = useMemo(() => {
    if (!periodDraft) return [];
    const duplicateIds = new Set(duplicateClaims.map((claim) => claim.id));
    return findOverlappingClaims(
      { from: periodDraft.valid_from, to: periodDraft.valid_to },
      existingClaims,
    ).filter((claim) => !duplicateIds.has(claim.id));
  }, [periodDraft, existingClaims, duplicateClaims]);

  const isFormValid = useMemo(() => {
    if (!formFrom || !formTo) return false;
    if (fromError || toError) return false;
    if (relational && organizationHasRelationalProducts !== true) return false;
    if (!relational && !parsedStinaProductId) return false;
    if (duplicateClaims.length > 0) return false;
    return true;
  }, [
    formFrom,
    formTo,
    fromError,
    toError,
    relational,
    organizationHasRelationalProducts,
    parsedStinaProductId,
    duplicateClaims.length,
  ]);

  const handleCreate = useCallback(async () => {
    if (!isFormValid) return;

    await onSubmit({
      valid_from: toPeriodStartTimestamp(formFrom),
      valid_to: toPeriodEndTimestamp(formTo, false),
      type: claimType,
      revoke_strategy: formRevokeStrategy,
      reissuance_allowed: true,
      relational: relational,
      ...(!relational && parsedStinaProductId
        ? { stina_product_id: parsedStinaProductId }
        : {}),
    });
  }, [
    isFormValid,
    onSubmit,
    formFrom,
    formTo,
    claimType,
    formRevokeStrategy,
    relational,
    parsedStinaProductId,
  ]);

  return (
    <DialogWindow
      open={open}
      onOpenChange={onOpenChange}
      title={formatMessage({ defaultMessage: 'Ny period' })}
      content={
        <div className="mx-auto flex w-full flex-col gap-10">
          <p className="text-text-900">
            <FormattedMessage defaultMessage="Skapa en ny period under vilken elever ska tilldelas biljetter. Under perioden kommer varje elev som mest ha en aktiv biljett åt gången som kommer från denna perioden. Är eleven med i andra aktiva perioder eller har fått biljetter manuellt utskickade så kommer dessa inte påverkas." />
          </p>

          <div className="flex flex-wrap gap-8">
            <Input
              id="period-from"
              label={formatMessage({ defaultMessage: 'Startdatum' })}
              type="date"
              value={formFrom}
              min={today}
              error={fromError}
              helperText={formatMessage({
                defaultMessage: 'Gäller från dagens början',
              })}
              onChange={(e) => setFormFrom(e.target.value)}
            />
            <Input
              id="period-to"
              label={formatMessage({ defaultMessage: 'Slutdatum' })}
              type="date"
              value={formTo}
              min={formFrom || today}
              error={toError}
              helperText={formatMessage({
                defaultMessage: 'Gäller till dagens slut',
              })}
              onChange={(e) => setFormTo(e.target.value)}
            />
          </div>

          {duplicateClaims.length > 0 && (
            <ErrorBanner id="duplicate-period">
              <FormattedMessage defaultMessage="Den här perioden finns redan." />
            </ErrorBanner>
          )}

          <PeriodOverlapWarning overlapping={overlappingClaims} />

          <div className="flex flex-col gap-4">
            {!isChildAndYouth && (
              <div className="flex flex-col gap-1">
                <p
                  id="period-ticket-kind-label"
                  className="text-base font-medium text-text-900"
                >
                  <FormattedMessage defaultMessage="Biljettyp" />
                </p>
                <ToggleButtonGroup
                  id="period-ticket-kind"
                  labelledBy="period-ticket-kind-label"
                  options={ticketKindOptions}
                  value={relational ? 'relational' : 'static'}
                  onChange={(value) => {
                    setFormRelational(value === 'relational');
                    setFormOfferStinaProductId(null);
                  }}
                />
                <p className="text-sm text-text-700">
                  {
                    ticketKindOptions.find(
                      (option) =>
                        option.value === (relational ? 'relational' : 'static'),
                    )?.tooltip
                  }
                </p>
                {relational && organizationHasRelationalProducts === false && (
                  <WarningBanner>
                    <FormattedMessage defaultMessage="Det finns inga relationsbundna biljetter tillgängliga i den här kategorin. Relationsbundna perioder kan därför inte skapas. Välj statisk biljett eller kontakta systemadministratören." />
                  </WarningBanner>
                )}
                {relational && tagsAccessFailed && (
                  <ErrorBanner>
                    <FormattedMessage defaultMessage="Kunde inte hämta vilka relationsbundna biljetter som är tillgängliga." />
                  </ErrorBanner>
                )}
              </div>
            )}

            {!relational && (
              <div className="flex flex-col gap-1">
                <label className="text-base font-medium text-text-900">
                  <FormattedMessage defaultMessage="Erbjudande" />
                </label>
                {offerSelectionUiMode === 'loading' && (
                  <>
                    <p className="text-text-700">
                      <FormattedMessage defaultMessage="Välj vilket erbjudande som ska användas för biljetter i denna period" />
                    </p>
                    <SelectableList
                      options={[]}
                      selectedOption=""
                      setSelectedOption={handleSelectOffer}
                      isLoading
                    />
                  </>
                )}
                {offerSelectionUiMode === 'empty' && (
                  <p className="italic text-text-700">
                    <FormattedMessage defaultMessage="Inga erbjudanden tillgängliga för denna kategori." />
                  </p>
                )}
                {offerSelectionUiMode === 'single' && (
                  <>
                    <p className="text-text-700">
                      <FormattedMessage defaultMessage="Detta erbjudande används för biljetter i denna period." />
                    </p>
                    <p className="text-text-900">{offerOptions[0].label}</p>
                  </>
                )}
                {offerSelectionUiMode === 'multiple' && (
                  <>
                    <p className="text-text-700">
                      <FormattedMessage defaultMessage="Välj vilket erbjudande som ska användas för biljetter i denna period" />
                    </p>
                    <SelectableList
                      options={offerOptions}
                      selectedOption={
                        formOfferStinaProductId ?? offerOptions[0]?.id ?? ''
                      }
                      setSelectedOption={handleSelectOffer}
                      noResultsMessage={formatMessage({
                        defaultMessage:
                          'Inga erbjudanden tillgängliga för denna kategori.',
                      })}
                    />
                  </>
                )}
                {offerFormatError && (
                  <p className="text-red-800" role="alert">
                    {offerFormatError}
                  </p>
                )}
              </div>
            )}
          </div>

          <div className="flex flex-col gap-1">
            <p
              id="period-revoke-strategy-label"
              className="text-base font-medium text-text-900"
            >
              <FormattedMessage defaultMessage="Makuleringsstrategi" />
            </p>
            <p className="text-text-700">
              <FormattedMessage defaultMessage="Bestämmer vad som händer med periodens ej förbrukade biljetter när perioden tar slut. Elever som tas bort eller inaktiveras innan perioden tar slut får alltid sin biljett makulerad." />
            </p>
            <ToggleButtonGroup
              id="period-revoke-strategy"
              labelledBy="period-revoke-strategy-label"
              options={revokeStrategyOptions}
              value={formRevokeStrategy}
              onChange={(value) =>
                setFormRevokeStrategy(
                  value as CollectionTicketClaimRevokeStrategyDTO,
                )
              }
            />
            <p className="text-sm text-text-700">
              {
                revokeStrategyOptions.find(
                  (option) => option.value === formRevokeStrategy,
                )?.tooltip
              }
            </p>
          </div>

          <div className="flex justify-end gap-2 pt-2">
            <Button variant="ghost" onClick={() => onOpenChange(false)}>
              <FormattedMessage defaultMessage="Avbryt" />
            </Button>
            <Button
              variant="filled"
              onClick={handleCreate}
              loading={isSubmitting}
              disabled={!isFormValid}
              aria-describedby={
                duplicateClaims.length > 0 ? 'duplicate-period' : undefined
              }
              startIcon={<IconCheck />}
            >
              <FormattedMessage defaultMessage="Skapa" />
            </Button>
          </div>
        </div>
      }
    />
  );
};
